/**
 * Agendar una cita. Se abre desde la ficha del paciente o desde dentro de una
 * atención — en ese segundo caso es un control o la cita de la derivación, y
 * hereda especialidad, sede y profesional de la consulta de origen.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createCita, type Cita, type CitaCreate } from "../../api/citas";
import { listAsignacionesDePaciente } from "../../api/asignaciones";
import { listTriajesDePaciente } from "../../api/triaje";
import { type SlotLibre } from "../../api/agenda";
import { listEspecialidades, listProfesionales } from "../../api/profesionales";
import { listSedes } from "../../api/tenants";
import { useAuth } from "../../auth/AuthProvider";
import { useTenantBranding, useZonaHoraria } from "../../auth/TenantBrandingProvider";
import { formatApiError } from "../../api/client";
import { hora } from "../../lib/fecha";
import { Alert, Button, Dialog, Field } from "../ui";
import { SelectorAgenda } from "./SelectorAgenda";

export function AgendarCitaDialog({
  pacienteId,
  open,
  onClose,
  citaPrevia,
  pacienteNombre,
  onCreated,
}: {
  pacienteId: number;
  open: boolean;
  onClose: () => void;
  /** Atención de la que nace esta cita (control o seguimiento). */
  citaPrevia?: Cita | null;
  /** Para confirmar a quién se le agenda cuando se llega desde el buscador. */
  pacienteNombre?: string;
  /** La cita recién creada, para que quien abrió el diálogo la muestre. */
  onCreated?: (c: Cita) => void;
}) {
  const tz = useZonaHoraria();
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const { branding } = useTenantBranding();
  const isSuperAdmin = !!me?.is_super_admin;
  const superAdminTenantId = isSuperAdmin ? branding?.id ?? null : null;

  const { data: asignaciones = [] } = useQuery({
    queryKey: ["asignaciones", pacienteId],
    queryFn: () => listAsignacionesDePaciente(pacienteId),
    enabled: open,
  });
  const { data: profesionales = [] } = useQuery({
    queryKey: ["profesionales", "all"],
    queryFn: () => listProfesionales(),
    staleTime: 60_000,
    enabled: open,
  });
  const { data: especialidades = [] } = useQuery({
    queryKey: ["especialidades"],
    queryFn: listEspecialidades,
    staleTime: 5 * 60_000,
    enabled: open,
  });
  const { data: sedes = [] } = useQuery({
    queryKey: ["sedes", isSuperAdmin ? `tenant-${superAdminTenantId}` : "self"],
    queryFn: () =>
      isSuperAdmin && superAdminTenantId != null
        ? listSedes()
        : listSedes(),
    staleTime: 5 * 60_000,
    enabled: open && (!isSuperAdmin || superAdminTenantId != null),
  });
  const { data: triajes = [] } = useQuery({
    queryKey: ["triajes", pacienteId],
    queryFn: () => listTriajesDePaciente(pacienteId),
    enabled: open,
  });

  const asignacionesActivas = asignaciones.filter((a) => a.estado === "ACTIVA");

  const [asignacionId, setAsignacionId] = useState<number | null>(null);
  const [profesionalId, setProfesionalId] = useState<number | null>(null);
  const [especialidadId, setEspecialidadId] = useState<number | null>(null);
  const [sedeId, setSedeId] = useState<number | null>(null);
  const [fecha, setFecha] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [motivo, setMotivo] = useState<string>("");
  const [slotElegido, setSlotElegido] = useState<SlotLibre | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    // Si venimos de una atención, la derivación que salió de ella manda; si no,
    // la primera asignación activa; y si tampoco, lo que traiga la cita previa.
    const deEstaCita = citaPrevia
      ? asignacionesActivas.find((a) => a.cita_origen_id === citaPrevia.id)
      : undefined;
    const a = deEstaCita ?? asignacionesActivas[0];
    if (a) {
      setAsignacionId(a.id);
      setEspecialidadId(a.especialidad_id);
      setProfesionalId(a.profesional_id);
      setSedeId(a.sede_id);
    } else if (citaPrevia) {
      setAsignacionId(null);
      setEspecialidadId(citaPrevia.especialidad_id);
      setProfesionalId(citaPrevia.profesional_id);
      setSedeId(citaPrevia.sede_id);
    } else {
      setAsignacionId(null);
      setEspecialidadId(null);
      setProfesionalId(null);
      setSedeId(null);
    }
    setMotivo("");
    setSlotElegido(null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, asignaciones.length, citaPrevia?.id]);

  function elegirAsignacion(idStr: string) {
    const id = idStr ? Number(idStr) : null;
    setAsignacionId(id);
    setSlotElegido(null);
    const a = asignaciones.find((x) => x.id === id);
    if (a) {
      setEspecialidadId(a.especialidad_id);
      setProfesionalId(a.profesional_id);
      setSedeId(a.sede_id);
    }
  }

  const profesionalesValidos = profesionales.filter((p) => {
    if (!p.activo) return false;
    if (especialidadId && !p.especialidades.some((e) => e.id === especialidadId))
      return false;
    if (sedeId && !p.sedes.some((s) => s.id === sedeId)) return false;
    return true;
  });

  const mutation = useMutation({
    mutationFn: (p: CitaCreate) => createCita(p),
    onSuccess: (creada) => {
      queryClient.invalidateQueries({ queryKey: ["citas", "paciente", pacienteId] });
      queryClient.invalidateQueries({ queryKey: ["citas"] });
      queryClient.invalidateQueries({ queryKey: ["mi-espacio"] });
      onClose();
      onCreated?.(creada);
    },
    onError: (err) => {
      setError(formatApiError(err, "No se pudo agendar la cita."));
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (
      profesionalId == null ||
      especialidadId == null ||
      sedeId == null ||
      slotElegido == null
    ) {
      setError("Selecciona profesional, especialidad, sede y un horario.");
      return;
    }
    // Solo enganchamos un triaje que aún no pertenece a ninguna atención: el de
    // quien llegó sin cita y se le tomó antes de agendar. Los demás ya tienen dueño.
    const triajeHuerfano = triajes.find((t) => t.cita_id == null) ?? null;
    mutation.mutate({
      paciente_id: pacienteId,
      profesional_id: profesionalId,
      especialidad_id: especialidadId,
      sede_id: sedeId,
      inicio: slotElegido.inicio,
      fin: slotElegido.fin,
      motivo: motivo.trim() || null,
      asignacion_id: asignacionId,
      triaje_id: triajeHuerfano?.id ?? null,
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={citaPrevia ? "Agendar control o cita relacionada" : "Agendar cita"}
      description={
        citaPrevia
          ? `Nace de la atención de ${citaPrevia.especialidad_nombre}. Cambia la especialidad si es una cita en otra área.`
          : pacienteNombre
            ? `Para ${pacienteNombre}.`
            : "Reserva un horario disponible del médico para este paciente."
      }
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        {asignacionesActivas.length > 0 && (
          <Field
            label="Derivación"
            htmlFor="ag_asig"
            hint="Una cita normalmente nace de una derivación ya hecha."
          >
            <select
              id="ag_asig"
              value={asignacionId ?? ""}
              onChange={(e) => elegirAsignacion(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">— sin derivación previa —</option>
              {asignacionesActivas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.especialidad_nombre}
                  {a.profesional_nombre
                    ? ` — ${a.profesional_nombre}`
                    : " (sin profesional)"}
                  {a.sede_nombre ? ` · ${a.sede_nombre}` : ""}
                </option>
              ))}
            </select>
          </Field>
        )}

        <div className="grid grid-cols-3 gap-3">
          <Field label="Especialidad *" htmlFor="ag_esp">
            <select
              id="ag_esp"
              value={especialidadId ?? ""}
              onChange={(e) => {
                setEspecialidadId(e.target.value ? Number(e.target.value) : null);
                setSlotElegido(null);
              }}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              required
            >
              <option value="">— seleccionar —</option>
              {especialidades.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nombre}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Sede *" htmlFor="ag_sede">
            <select
              id="ag_sede"
              value={sedeId ?? ""}
              onChange={(e) => {
                setSedeId(e.target.value ? Number(e.target.value) : null);
                setSlotElegido(null);
              }}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              required
            >
              <option value="">— seleccionar —</option>
              {sedes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Profesional *" htmlFor="ag_prof">
            <select
              id="ag_prof"
              value={profesionalId ?? ""}
              onChange={(e) => {
                setProfesionalId(e.target.value ? Number(e.target.value) : null);
                setSlotElegido(null);
              }}
              disabled={especialidadId == null}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-400"
              required
            >
              <option value="">
                {especialidadId == null
                  ? "— elige especialidad primero —"
                  : profesionalesValidos.length === 0
                    ? "— sin profesionales —"
                    : "— seleccionar —"}
              </option>
              {profesionalesValidos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre_completo}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {/* Calendario y jornada, el mismo selector que usan las sesiones de un
            tratamiento. El campo de fecha suelto obligaba a ir probando días
            hasta dar con uno en que el profesional atendiera. */}
        <SelectorAgenda
          profesionalId={profesionalId}
          sedeId={sedeId}
          dia={fecha}
          onDiaChange={(d) => {
            setFecha(d);
            setSlotElegido(null);
          }}
          onElegirSlot={setSlotElegido}
          slotElegidoInicio={slotElegido?.inicio ?? null}
        />

        {slotElegido && (
          <p className="rounded-lg bg-[color-mix(in_srgb,var(--color-link)_8%,white)] px-3 py-2 text-sm text-slate-700">
            Cita el{" "}
            <span className="font-medium">
              {new Date(slotElegido.inicio).toLocaleString("es-PE", {
                timeZone: tz,
                weekday: "long",
                day: "2-digit",
                month: "long",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>{" "}
            · {hora(slotElegido.inicio, tz)} a {hora(slotElegido.fin, tz)}
          </p>
        )}

        <Field label="Motivo / nota" htmlFor="ag_mot">
          <textarea
            id="ag_mot"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={2}
            maxLength={4000}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            placeholder={
              citaPrevia
                ? "Control post-consulta, resultado de exámenes, etc."
                : "Motivo de la consulta o nota para el médico."
            }
          />
        </Field>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            loading={mutation.isPending}
            disabled={slotElegido == null}
          >
            Agendar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
